import React from 'react'
import { styled } from '@mui/material/styles'
import { Text } from '../components/Text'

type Props = {
  /**
   * The current amount raised towards the goal, in cents.
   */
  amount: number
  /**
   * The target amount to be reached, in cents.
   */
  target: number
  /**
   * Determines whether to show labels above the tracker displaying the raised amount and goal amount.
   * @default true
   */
  showLabels?: boolean
  /**
   * Determines whether to show a note below the tracker
   * @default true
   */
  showNote?: boolean
  /**
   * Additional progress amount, in cents to be shown in the tracker.
   * @default 0
   */
  progress?: number
}

/**
 * GoalTracker Component
 *
 * Displays a goal tracker with current amount raised and progress towards the target goal.
 * The component supports optional labels, progress bar, and a note to show appreciation to the user.
 *
 * @param {Props} props - The component props.
 * @returns {JSX.Element} - A React functional component representing the GoalTracker.
 */
export const GoalTracker = ({
  amount = 0,
  target,
  progress = 0,
  showLabels = true,
  showNote = true,
}: Props): JSX.Element => {
  const progressWidth = progress ? ((amount + progress) / target) * 100 : 0
  const progressPercent = Math.min((progress / target) * 100, 100)
  return (
    <>
      {showLabels && (
        <LabelContainer>
          <Text>
            Raised:{' '}
            {new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              maximumFractionDigits: 0,
            }).format(amount / 100)}
          </Text>
          {!!target && (
            <Text>
              Goal:{' '}
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0,
              }).format(target / 100)}
            </Text>
          )}
        </LabelContainer>
      )}

      <GoalContainer>
        <CurrentAmount width={(amount / target) * 100} />
        <Progress width={progressWidth} />
      </GoalContainer>
      {showNote && amount < target && (
        <NoteContainer>
          <Text variant='p3'>
            {progressPercent >= 1 ? (
              <>
                Your current selection gets us {progressPercent.toFixed(0)}%
                closer to reaching our goal; thank you for your support!
              </>
            ) : (
              <>
                Your current selection gets us{' '}
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  maximumFractionDigits: 2,
                }).format(progress / 100)}{' '}
                closer to reaching our goal; thank you for your support
              </>
            )}
          </Text>
        </NoteContainer>
      )}
    </>
  )
}

const LabelContainer = styled('div')`
  display: flex;
  justify-content: space-between;
  color: #fff;
  width: 100%;
`

const NoteContainer = styled('div')`
  color: #fff;
  padding: 12px 12px 12px 16px;
  border: 1px solid #fff;
  border-radius: 4px;
  width: 100%;
  box-sizing: border-box;
`

const Shared = styled('div')`
  height: 24px;
  border-radius: 100px;
  background-color: rgba(255, 255, 255, 0.25);
  position: absolute;
`

const GoalContainer = styled(Shared)`
  width: 100%;
  position: relative;
  overflow: hidden;
  margin: 12px 0;
`

const CurrentAmount = styled(Shared)<{
  width: number
}>`
  width: ${({ width }) => `${Math.min(width, 100)}%`};
  background-color: #fff;
  transition: width 0.75s ease-in-out;
`

const Progress = styled(Shared)<{
  width: number
}>`
  width: ${({ width }) => `${Math.min(width, 100)}%`};
  transition: width 0.75s ease-in-out;
`
